import { useState } from "react";
import { Check, Clock, X, ChevronLeft, ChevronRight, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useCmsContent } from "@/hooks/useCmsContent";
import { getBookingUrl } from "@/lib/booking";

interface RoomImage { src: string; alt: string; }
interface RoomAmenity { icon: LucideIcon; label: string; }
interface RoomHighlight { image: string; title: string; subtitle: string; }
interface RoomDetailProps { title: string; subtitle: string; images: RoomImage[]; amenities: RoomAmenity[]; features: readonly string[]; highlight?: RoomHighlight; }

const RoomDetail = ({ title, subtitle, images, amenities, features, highlight }: RoomDetailProps) => {
  const { t, language } = useLanguage();
  const { get } = useCmsContent();
  const [selectedImage, setSelectedImage] = useState<number | null>(null);

  const navigateImage = (direction: "prev" | "next") => {
    if (selectedImage === null) return;
    if (direction === "prev") setSelectedImage(selectedImage === 0 ? images.length - 1 : selectedImage - 1);
    else setSelectedImage(selectedImage === images.length - 1 ? 0 : selectedImage + 1);
  };

  return (
    <>
      <section className="hotel-section bg-card">
        <div className="hotel-container">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center mb-12 md:mb-20">
            <div className="relative h-[280px] md:h-[500px] rounded-lg overflow-hidden shadow-elevated cursor-pointer group" onClick={() => setSelectedImage(0)}>
              <img src={images[0]?.src} alt={title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-foreground/0 group-hover:bg-foreground/10 transition-colors duration-300" />
              <div className="absolute bottom-4 right-4 bg-background/90 backdrop-blur-sm px-4 py-2 rounded-md">
                <p className="font-body text-xs text-foreground font-medium">{images.length} {get("room_detail_view_photos") || t.roomDetail.viewPhotos}</p>
              </div>
            </div>
            <div className="space-y-8">
              <div className="space-y-3">
                <p className="hotel-caption text-accent">{get("room_detail_our_rooms") || t.roomDetail.ourRooms}</p>
                <h2 className="font-display text-3xl md:text-5xl font-light text-foreground leading-tight">{title}</h2>
                <p className="font-display text-lg italic text-accent">{subtitle}</p>
              </div>
              <div className="w-16 h-px bg-accent" />
              <p className="hotel-body text-muted-foreground">{get("room_detail_book_direct") || t.roomDetail.bookDirect}</p>
              <p className="font-body text-sm text-muted-foreground italic">{get("room_detail_note") || t.roomDetail.note}</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button variant="hotel" asChild><a href={getBookingUrl(language)} target="_blank" rel="noopener noreferrer">{get("room_detail_book_now") || t.roomDetail.bookNow}</a></Button>
                <Button variant="outline" asChild>
                  <a href="https://wa.me/41772581052" target="_blank" rel="noopener noreferrer"><MessageCircle className="w-4 h-4 mr-2" />WhatsApp</a>
                </Button>
              </div>
            </div>
          </div>
          <div className="mb-12 md:mb-20">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
              {images.map((image, index) => (
                <div key={index} className={`relative overflow-hidden rounded-lg group cursor-pointer ${index === 0 ? "col-span-2 row-span-2 h-[200px] md:h-[400px]" : index === images.length - 1 && images.length > 4 ? "col-span-2 h-[150px] md:h-[200px]" : "h-[150px] md:h-[200px]"}`} onClick={() => setSelectedImage(index)}>
                  <img src={image.src} alt={image.alt} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-foreground/0 group-hover:bg-foreground/20 transition-colors duration-300" />
                  <div className="absolute bottom-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <p className="font-body text-xs text-primary-foreground font-medium bg-foreground/50 backdrop-blur-sm px-2 py-1 rounded">{image.alt}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16">
            <div>
              <h3 className="font-display text-2xl md:text-3xl font-light text-foreground mb-8">{get("room_detail_equipment") || t.roomDetail.equipment}</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {amenities.map((amenity) => (
                  <div key={amenity.label} className="flex flex-col items-center text-center p-5 bg-background rounded-lg shadow-soft">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-3"><amenity.icon className="w-5 h-5 text-primary" strokeWidth={1.5} /></div>
                    <p className="font-body text-sm text-foreground font-medium">{amenity.label}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-6">
              <div className="bg-background p-6 rounded-lg shadow-soft">
                <div className="flex items-center gap-3 mb-4"><div className="p-2 bg-accent/10 rounded-lg"><Clock className="w-5 h-5 text-accent" /></div><h4 className="font-display text-lg font-semibold text-foreground">{get("room_detail_hotel_times") || t.roomDetail.hotelTimes}</h4></div>
                <div className="space-y-2">
                  <div className="flex justify-between font-body text-sm"><span className="text-muted-foreground">Check-in</span><span className="text-foreground font-medium">16:00 – 22:00</span></div>
                  <div className="w-full h-px bg-border" />
                  <div className="flex justify-between font-body text-sm"><span className="text-muted-foreground">Check-out</span><span className="text-foreground font-medium">07:00 – 11:00</span></div>
                </div>
              </div>
              {highlight && (
                <div className="relative h-[250px] rounded-lg overflow-hidden shadow-elevated">
                  <img src={highlight.image} alt={highlight.title} loading="lazy" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground/60 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <p className="font-display text-lg font-semibold text-primary-foreground">{highlight.title}</p>
                    <p className="font-body text-sm text-primary-foreground/80">{highlight.subtitle}</p>
                  </div>
                </div>
              )}
              <div className="text-center"><Button variant="outline" asChild><Link to="/rooms">{get("room_detail_all_rooms") || t.roomDetail.allRooms}</Link></Button></div>
            </div>
          </div>
        </div>
      </section>
      {selectedImage !== null && (
        <div className="fixed inset-0 z-50 bg-foreground/90 flex items-center justify-center p-4" onClick={() => setSelectedImage(null)}>
          <button className="absolute top-4 right-4 text-primary-foreground hover:text-accent transition-colors z-10" onClick={() => setSelectedImage(null)}><X className="w-8 h-8" /></button>
          <button className="absolute left-4 top-1/2 -translate-y-1/2 text-primary-foreground hover:text-accent transition-colors z-10" onClick={(e) => { e.stopPropagation(); navigateImage("prev"); }}><ChevronLeft className="w-10 h-10" /></button>
          <button className="absolute right-4 top-1/2 -translate-y-1/2 text-primary-foreground hover:text-accent transition-colors z-10" onClick={(e) => { e.stopPropagation(); navigateImage("next"); }}><ChevronRight className="w-10 h-10" /></button>
          <div className="max-w-5xl max-h-[90vh] relative" onClick={(e) => e.stopPropagation()}>
            <img src={images[selectedImage].src} alt={images[selectedImage].alt} loading="lazy" className="max-w-full max-h-[85vh] object-contain rounded-lg" />
            <div className="absolute bottom-0 left-0 right-0 bg-background/90 backdrop-blur-sm p-4 rounded-b-lg">
              <p className="font-display text-lg font-semibold text-foreground text-center">{images[selectedImage].alt}</p>
              <p className="font-body text-xs text-muted-foreground text-center mt-1">{selectedImage + 1} / {images.length}</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RoomDetail;
